import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { OutlineNode } from '../../data/outline';

const MAX_DEPTH = 3;

type Path = number[];

/** Every node's path key (including leaves), so "collapse all" can hide summary/example too. */
function collectPathKeys(nodes: OutlineNode[], prefix: Path = []): string[] {
  return nodes.flatMap((node, i) => {
    const path = [...prefix, i];
    return [path.join('.'), ...collectPathKeys(node.subheadings, path)];
  });
}

function updateAt(nodes: OutlineNode[], path: Path, updater: (node: OutlineNode) => OutlineNode): OutlineNode[] {
  const [index, ...rest] = path;
  return nodes.map((node, i) => {
    if (i !== index) return node;
    return rest.length === 0 ? updater(node) : { ...node, subheadings: updateAt(node.subheadings, rest, updater) };
  });
}

function removeAt(nodes: OutlineNode[], path: Path): OutlineNode[] {
  const [index, ...rest] = path;
  if (rest.length === 0) return nodes.filter((_, i) => i !== index);
  return nodes.map((node, i) => (i === index ? { ...node, subheadings: removeAt(node.subheadings, rest) } : node));
}

function addChildAt(nodes: OutlineNode[], path: Path): OutlineNode[] {
  const [index, ...rest] = path;
  return nodes.map((node, i) => {
    if (i !== index) return node;
    if (rest.length === 0) return { ...node, subheadings: [...node.subheadings, { title: '', summary: '', subheadings: [] }] };
    return { ...node, subheadings: addChildAt(node.subheadings, rest) };
  });
}

function moveAt(nodes: OutlineNode[], path: Path, direction: -1 | 1): OutlineNode[] {
  const [index, ...rest] = path;
  if (rest.length === 0) {
    const target = index + direction;
    if (target < 0 || target >= nodes.length) return nodes;
    const next = [...nodes];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  }
  return nodes.map((node, i) => (i === index ? { ...node, subheadings: moveAt(node.subheadings, rest, direction) } : node));
}


interface OutlineTreeEditorProps {
  outline: OutlineNode[];
  onChange: (outline: OutlineNode[]) => void;
}

/** Rename/add/remove/reorder-within-parent only — promoting/demoting a node's depth is out of
 * scope for v1 (ask the chat to restructure instead). */
export function OutlineTreeEditor({ outline, onChange }: OutlineTreeEditorProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  // Absence from the set means collapsed — new/AI-generated sections are collapsed by default.
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const toggleCollapse = (path: Path) => {
    const key = path.join('.');
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex rounded border border-input bg-surface p-0.5" role="tablist" aria-label={t('outline.tree.modeLabel')}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'preview'}
            onClick={() => setMode('preview')}
            className={`rounded-sm px-2.5 py-1 text-xs font-semibold transition ${
              mode === 'preview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('outline.tree.previewMode')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'edit'}
            onClick={() => setMode('edit')}
            className={`rounded-sm px-2.5 py-1 text-xs font-semibold transition ${
              mode === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('outline.tree.editMode')}
          </button>
        </div>

        {mode === 'edit' && outline.length > 0 ? (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setExpandedPaths(new Set(collectPathKeys(outline)))}
              className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
            >
              {t('outline.tree.expandAll')}
            </button>
            <button
              type="button"
              onClick={() => setExpandedPaths(new Set())}
              className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
            >
              {t('outline.tree.collapseAll')}
            </button>
          </div>
        ) : null}
      </div>

      {outline.length === 0 ? <p className="text-sm text-muted-foreground">{t('outline.tree.empty')}</p> : null}

      {outline.length > 0 && mode === 'preview' ? (
        <ol className="space-y-1.5">
          {outline.map((node, index) => (
            <PreviewNodeRow key={index} node={node} depth={1} />
          ))}
        </ol>
      ) : null}

      {outline.length > 0 && mode === 'edit' ? (
        <ol className="space-y-2">
          {outline.map((node, index) => (
            <OutlineNodeRow
              key={index}
              node={node}
              path={[index]}
              depth={1}
              index={index}
              siblingCount={outline.length}
              expandedPaths={expandedPaths}
              onToggleCollapse={toggleCollapse}
              onUpdate={(path, updater) => onChange(updateAt(outline, path, updater))}
              onRemove={(path) => onChange(removeAt(outline, path))}
              onAddChild={(path) => onChange(addChildAt(outline, path))}
              onMove={(path, direction) => onChange(moveAt(outline, path, direction))}
            />
          ))}
        </ol>
      ) : null}

      {mode === 'edit' ? (
        <button
          type="button"
          onClick={() => onChange([...outline, { title: '', summary: '', subheadings: [] }])}
          className="inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          {t('outline.tree.addSection')}
        </button>
      ) : null}
    </div>
  );
}

/** Read-only heading tree for Preview mode — summary/example surface as a hover tooltip instead of inline fields. */
function PreviewNodeRow({ node, depth }: { node: OutlineNode; depth: number }) {
  const { t } = useTranslation();
  const title = node.title || t('outline.tree.untitled');
  const hasDetail = Boolean(node.summary || node.example);

  return (
    <li className={INDENT_BY_DEPTH[depth] ?? ''}>
      <div className="group relative inline-block">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {hasDetail ? (
          <div className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden w-72 max-w-[80vw] rounded-lg bg-panel-dark p-3 text-xs text-panel-dark-foreground shadow-lg group-hover:block">
            {node.summary ? <p>{node.summary}</p> : null}
            {node.example ? <p className="mt-2 italic opacity-80">{node.example}</p> : null}
          </div>
        ) : null}
      </div>

      {node.subheadings.length > 0 ? (
        <ol className="mt-1.5 space-y-1.5">
          {node.subheadings.map((child, childIndex) => (
            <PreviewNodeRow key={childIndex} node={child} depth={depth + 1} />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

interface OutlineNodeRowProps {
  node: OutlineNode;
  path: Path;
  depth: number;
  index: number;
  siblingCount: number;
  expandedPaths: Set<string>;
  onToggleCollapse: (path: Path) => void;
  onUpdate: (path: Path, updater: (node: OutlineNode) => OutlineNode) => void;
  onRemove: (path: Path) => void;
  onAddChild: (path: Path) => void;
  onMove: (path: Path, direction: -1 | 1) => void;
}

const INDENT_BY_DEPTH: Record<number, string> = { 1: '', 2: 'ml-4', 3: 'ml-8' };

function OutlineNodeRow({
  node,
  path,
  depth,
  index,
  siblingCount,
  expandedPaths,
  onToggleCollapse,
  onUpdate,
  onRemove,
  onAddChild,
  onMove,
}: OutlineNodeRowProps) {
  const { t } = useTranslation();
  const title = node.title || t('outline.tree.untitled');
  const isCollapsed = !expandedPaths.has(path.join('.'));


  return (
    <li className={`rounded border border-border bg-background p-2 ${INDENT_BY_DEPTH[depth] ?? ''}`}>
      <div className="flex items-start gap-2">
        <button
          type="button"
          aria-label={isCollapsed ? t('outline.tree.expandSection', { title }) : t('outline.tree.collapseSection', { title })}
          aria-expanded={!isCollapsed}
          onClick={() => onToggleCollapse(path)}
          className="mt-1 rounded p-0.5 text-muted-foreground transition hover:text-foreground"
        >
          {isCollapsed ? <ChevronRightIcon className="size-4" aria-hidden="true" /> : <ChevronDownIcon className="size-4" aria-hidden="true" />}
        </button>

        <div className="flex flex-col">
          <button
            type="button"
            aria-label={t('outline.tree.moveUp', { title })}
            disabled={index === 0}
            onClick={() => onMove(path, -1)}
            className="rounded p-0.5 text-muted-foreground transition hover:text-foreground disabled:opacity-30"
          >
            <ChevronUpIcon className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={t('outline.tree.moveDown', { title })}
            disabled={index === siblingCount - 1}
            onClick={() => onMove(path, 1)}
            className="rounded p-0.5 text-muted-foreground transition hover:text-foreground disabled:opacity-30"
          >
            <ChevronDownIcon className="size-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 space-y-1">
          <input
            type="text"
            value={node.title}
            onChange={(event) => onUpdate(path, (n) => ({ ...n, title: event.target.value }))}
            placeholder={t('outline.tree.titlePlaceholder')}
            aria-label={t('outline.tree.titleLabel')}
            className="w-full rounded-sm border border-input bg-surface px-2 py-1 text-sm font-medium outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          />
          {!isCollapsed ? (
            <>
              <textarea
                value={node.summary}
                onChange={(event) => onUpdate(path, (n) => ({ ...n, summary: event.target.value }))}
                placeholder={t('outline.tree.summaryPlaceholder')}
                aria-label={t('outline.tree.summaryLabel')}
                rows={2}
                className="w-full rounded-sm border border-input bg-surface px-2 py-1 text-xs outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
              />
              <textarea
                value={node.example ?? ''}
                onChange={(event) => onUpdate(path, (n) => ({ ...n, example: event.target.value }))}
                placeholder={t('outline.tree.examplePlaceholder')}
                aria-label={t('outline.tree.exampleLabel')}
                rows={2}
                className="w-full rounded-sm border border-input bg-surface px-2 py-1 text-xs outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
              />
            </>
          ) : null}
        </div>

        <button
          type="button"
          aria-label={t('outline.tree.remove', { title })}
          onClick={() => onRemove(path)}
          className="rounded p-1 text-muted-foreground transition hover:text-destructive"
        >
          <TrashIcon className="size-4" aria-hidden="true" />
        </button>
      </div>

      {node.subheadings.length > 0 ? (
        <ol className="mt-2 space-y-2">
          {node.subheadings.map((child, childIndex) => (
            <OutlineNodeRow
              key={childIndex}
              node={child}
              path={[...path, childIndex]}
              depth={depth + 1}
              index={childIndex}
              siblingCount={node.subheadings.length}
              expandedPaths={expandedPaths}
              onToggleCollapse={onToggleCollapse}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onAddChild={onAddChild}
              onMove={onMove}
            />
          ))}
        </ol>
      ) : null}


      {depth < MAX_DEPTH ? (
        <button
          type="button"
          onClick={() => onAddChild(path)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition hover:text-foreground"
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
          {t('outline.tree.addSubsection')}
        </button>
      ) : null}
    </li>
  );
}
